import { useCallback } from 'react'
import { Button } from '@/atoms/Button'
import { ArrowRightIcon } from '@/atoms/Icons/ArrowRightIcon'
import { CircleCheckIcon } from '@/atoms/Icons/CircleCheckIcon'
import { AsH2, HeadingSM, ParagraphMD } from '@/atoms/Text'
import { ANGEL_STUDIOS_EMAIL_GROUP_ID } from '@/constants/email'
import { slugs } from '@/constants/slugs'
import { useToast } from '@/molecules/Toast'
import { useSafeTrack } from '@/utils/analytics'
import { Translate, useTranslate } from '@/utils/translate/translate-client'
import { EmailCaptureFormInput } from '@/views/SignUpView/EmailCapture/EmailCaptureFormInput/EmailCaptureFormInput'

export function EmailCaptureSection() {
  const { t } = useTranslate('music')
  const track = useSafeTrack()

  const { showToast } = useToast()

  const handleSubmit = useCallback(() => {
    showToast(t('thankYouForSigningup', "Thank you for signing up. We'll be in touch as there's news to share"), {
      shouldHideClose: true,
      icon: <CircleCheckIcon />,
    })
    track('Music Page Email Capture Form Submitted')
  }, [showToast, t, track])

  return <EmailCaptureSectionForm onSubmit={handleSubmit} />
}

interface EmailCaptureSectionFormProps {
  onSubmit: () => void
}
function EmailCaptureSectionForm(props: EmailCaptureSectionFormProps) {
  const { t } = useTranslate('music')
  const track = useSafeTrack()
  const updatesText = t('signUpUpdatesAngel', 'Sign up to get Notifications from Angel')

  const handleEmailClicked = useCallback(() => {
    track('Music Page Email Link Clicked')
  }, [track])

  return (
    <div id="signup-cta" className="mt-36 px-4 pt-20 text-center text-white md:mt-40 md:py-28">
      <div className="mb-8">
        <HeadingSM id="get-updates-title" as={AsH2} weight="bold" className="mb-2">
          {t('getUpdates', 'Get Updates')}
        </HeadingSM>
        <ParagraphMD>{updatesText}</ParagraphMD>
      </div>
      <EmailCaptureFormInput
        onSubmit={props.onSubmit}
        position="hub-mid-page"
        subscriptionGroupId={ANGEL_STUDIOS_EMAIL_GROUP_ID}
        projectSlug={slugs.angelStudios}
        placeholder={t('enterEmail', 'Enter Email')}
        formClassName="relative max-w-lg mx-auto"
        inputClassName="rounded-lg w-full p-4 text-core-gray-950"
        formAriaLabel={t('getUpdates', 'Get Updates')}
      >
        <Button
          aria-label={t('submitEmail', 'Submit Email')}
          variant="black"
          className="absolute right-2 top-1/2 -translate-y-1/2 rounded-lg bg-black px-2"
        >
          <ArrowRightIcon color="white" />
        </Button>
      </EmailCaptureFormInput>
      <ParagraphMD className="mt-8">
        <Translate t={t} i18nKey="orEmailUsAngelMusicCom">
          Contact us at{' '}
          <a className="underline" href="mailto:music@angel.com" onClick={handleEmailClicked}>
            music@angel.com
          </a>
        </Translate>
      </ParagraphMD>
    </div>
  )
}
